// Cross-Module Event Queue - queue management, processing, and retry logic
// extracted from CrossModuleEventRouter for single-concern decomposition.

import { EventBus } from "../events/EventBus";
import debugLogger from "../utils/debugLogger";

import {
  ModuleInteraction,
  EventRoutingPattern,
  IntegrationConfig,
  IntegrationMetrics,
  CrossModulePayload,
} from "./types";

/**
 * Shared mutable state passed in from the CrossModuleIntegration facade.
 * The router mutates these maps directly to preserve existing behavior.
 */
export interface EventRouterState {
  eventBus: EventBus;
  config: IntegrationConfig;
  metrics: IntegrationMetrics;
  moduleInteractions: Map<string, ModuleInteraction[]>;
  eventRoutingPatterns: Map<string, EventRoutingPattern>;
  eventQueue: Array<{
    event: string;
    payload: CrossModulePayload;
    timestamp: number;
    priority: number;
  }>;
  isProcessingQueue: boolean;
  eventRetryMap: Map<string, { retries: number; lastAttempt: number }>;
  /**
   * Optional callback invoked when a module is registered via the
   * MODULE_REGISTERED global event. The facade wires this to the state sync
   * module so module state is initialized.
   */
  onModuleRegistered?: (moduleName: string) => void;
  /**
   * Optional callback invoked when a module is unregistered via the
   * MODULE_UNREGISTERED global event. The facade wires this to the state sync
   * and health modules so their tracking maps are cleaned up.
   */
  onModuleUnregistered?: (moduleName: string) => void;
  /**
   * Optional callback invoked after a cross-module interaction completes
   * (success or failure). The facade wires this to the health module so
   * performance metrics are updated.
   */
  onInteractionComplete?: (
    moduleName: string,
    latency: number,
    success: boolean,
  ) => void;
}

/**
 * Event queue: handles priority queueing, batched processing, and retry
 * logic. Mutates the shared state passed in.
 */
export class CrossModuleEventQueue {
  constructor(private state: EventRouterState) {}

  public addToEventQueue(
    event: string,
    payload: CrossModulePayload,
    priority: number = 1,
  ): void {
    const { state } = this;

    if (state.eventQueue.length >= state.config.maxQueueSize) {
      // Remove lowest priority events if queue is full
      state.eventQueue.sort((a, b) => b.priority - a.priority);
      state.eventQueue = state.eventQueue.slice(
        0,
        state.config.maxQueueSize - 1,
      );
    }

    state.eventQueue.push({
      event,
      payload,
      timestamp: Date.now(),
      priority,
    });

    state.metrics.eventQueueSize = state.eventQueue.length;

    // Start processing if not already processing
    if (!state.isProcessingQueue) {
      void this.processEventQueue();
    }
  }

  public async processEventQueue(): Promise<void> {
    const { state } = this;

    if (state.isProcessingQueue) {
      return;
    }

    state.isProcessingQueue = true;

    try {
      while (state.eventQueue.length > 0) {
        // Process events in batches
        const batch = state.eventQueue.splice(
          0,
          state.config.batchProcessingSize,
        );

        // Process batch in parallel
        await Promise.allSettled(
          batch.map((eventData) => this.processEvent(eventData)),
        );

        // Yield to UI thread
        await new Promise((resolve) => setTimeout(resolve, 0));
      }
    } finally {
      state.isProcessingQueue = false;
    }
  }

  public async handleEventRetry(
    interaction: ModuleInteraction,
    payload: CrossModulePayload,
    _error: unknown,
  ): Promise<void> {
    const { state } = this;
    const retryKey = `${interaction.id}_${payload.eventId || "unknown"}`;
    const retryInfo = state.eventRetryMap.get(retryKey) || {
      retries: 0,
      lastAttempt: 0,
    };

    if (retryInfo.retries < state.config.maxEventRetries) {
      retryInfo.retries++;
      retryInfo.lastAttempt = Date.now();
      state.eventRetryMap.set(retryKey, retryInfo);

      state.metrics.retryAttempts++;

      // Exponential backoff
      const delay = Math.pow(2, retryInfo.retries) * 1000;

      setTimeout(() => {
        void interaction
          .handler(payload)
          .then(() => {
            state.eventRetryMap.delete(retryKey);
          })
          .catch((retryError) => {
            debugLogger.error(
              "integration",
              `Event retry failed: ${interaction.id}`,
              retryError,
            );
          });
      }, delay);
    } else {
      debugLogger.error(
        "integration",
        `Max retries exceeded for event: ${interaction.id}`,
      );
      state.eventRetryMap.delete(retryKey);
    }
  }

  private async processEvent(eventData: {
    event: string;
    payload: CrossModulePayload;
    timestamp: number;
    priority: number;
  }): Promise<void> {
    try {
      // Find handlers for this event
      const handlers = this.findEventHandlers(eventData.event);

      if (handlers.length === 0) {
        debugLogger.warn(
          "integration",
          `No handlers found for event: ${eventData.event}`,
        );
        return;
      }

      // Execute handlers
      await Promise.allSettled(
        handlers.map((handler) => handler(eventData.payload)),
      );
    } catch (error) {
      debugLogger.error(
        "integration",
        `Failed to process event: ${eventData.event}`,
        error,
      );
    }
  }

  private findEventHandlers(
    event: string,
  ): Array<(payload: CrossModulePayload) => Promise<void>> {
    const { state } = this;
    const handlers: Array<(payload: CrossModulePayload) => Promise<void>> = [];

    state.moduleInteractions.forEach((moduleInteractions) => {
      moduleInteractions.forEach((interaction) => {
        const eventKey = `${interaction.sourceModule}:${interaction.eventType}`;
        if (eventKey === event) {
          handlers.push(interaction.handler);
        }
      });
    });

    return handlers;
  }
}
